<script>
import { mapActions, mapGetters } from 'vuex'
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import { OrganisationLevel } from '@/constants/admin/OrganisationLevel'
import SimpleOrgTreeNode from '@/models/admin/simpleOrgTreeNode'
import { cloneDeep, isEqual } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import { generateRandomName } from '@/helpers/string-helpers'
import { nanoid } from 'nanoid'
import toasts from '@/services/toasts/index'
import {
  findNodesByList,
  getNodesByLevel as getNodesByLevelFunc,
  searchNodesByLevel as searchNodesByLevelFunc,
  pruneChildrenNodesAtLevel,
  searchTree,
  filterDuplicateAdminNodes,
} from '@/helpers/org-tree-helpers'
import EmptyStateBlock from './empty-state-block.vue'
import ManagePresetsTable from './filter/manage-presets-table.vue'
import TruncatedChipTooltip from './truncated-chip-tooltip.vue'
import PresetSavedSuccessView from './filter/preset-saved-success-view.vue'
import DeletePresetConfirmationView from './filter/delete-preset-confirmation-view.vue'
import EmptySearchResultsBlock from './empty-search-results-block.vue'
import SavePresetForm from './filter/save-preset-form.vue'
import TruncatedChipList from './truncated-chip-list.vue'

/**
 * Custom level for the 'Mine' button
 */
const MINE_LEVEL = 'Mine'
/**
 * Custom level for the tree selection preview button
 */
const TREE_LEVEL = 'Tree'

const ViewKeys = Object.freeze({
  /**
   * Displays a chip group of all the selected items in the tree view
   */
  ChipSelection: 'ChipSelection',
  /**
   * Displays menu where users can save their current selection as a preset
   */
  SavePreset: 'SavePreset',
  /**
   * View displayed after a successful preset save
   */
  PresetSaveSuccess: 'PresetSaveSuccess',
  /**
   * Displays list of all presets
   */
  ManagePresets: 'ManagePresets',
  /**
   * Displays a confirmation message upon selecting a preset to delete
   */
  DeletePreset: 'DeletePreset',
})

/**
 * Initialises the view controls data prop based off of the ViewKeys obj
 */
const initViewControls = () => {
  const viewControls = {}

  for (const key in ViewKeys) {
    viewControls[key] = false
  }

  return viewControls
}

export default {
  name: 'OrganisationTreeSelector',
  components: {
    EmptyStateBlock,
    EmptySearchResultsBlock,
    ManagePresetsTable,
    TruncatedChipTooltip,
    PresetSavedSuccessView,
    DeletePresetConfirmationView,
    SavePresetForm,
    TruncatedChipList,
  },
  mixins: [baseMixin],
  props: {
    disabled: {
      type: Boolean,
    },
    /**
     * Reduces the height on the buttons
     */
    dense: {
      type: Boolean,
    },
    /**
     * Enables multiple selections within the tree filter.
     *
     * Checkboxes will be displayed within the treeview.
     */
    multiple: {
      type: Boolean,
    },
    /**
     * Treeview now supports two different selection types. The default type is
     * ‘leaf’, which will only include leaf nodes in the v-model array, but will
     * render parent nodes as either partially or fully selected.
     *
     * The alternative mode is ‘independent’, which allows one to select parent
     * nodes, but each node is independent of its parent and children.
     */
    selectionType: {
      type: String,
      default: 'leaf',
      validator: (value) => {
        const validOptions = ['leaf', 'independent']

        if (!value) return true
        return validOptions.includes(value.toLowerCase())
      },
    },
    loading: {
      type: Boolean,
    },
    /**
     * Enables a search filter within the tree filter dialog
     */
    searchable: {
      type: Boolean,
    },
    /**
     * Hides the action buttons
     */
    hideActions: {
      type: Array,
      default: () => [],
      validator: (actionsList) => {
        const validActions = [
          MINE_LEVEL.toLowerCase(),
          OrganisationLevel.Team.toLowerCase(),
          OrganisationLevel.Division.toLowerCase(),
          OrganisationLevel.Country.toLowerCase(),
          TREE_LEVEL.toLowerCase(),
        ]
        return actionsList.every((action) =>
          validActions.includes(action.toLowerCase())
        )
      },
    },
    /**
     * Orders the action buttons in ascending or descending order.
     *
     * asc (default):
     * Mine -> Team -> Division -> Country
     *
     * desc:
     * Country -> Division -> Team -> Mine
     */
    orderActions: {
      type: String,
      default: 'asc',
      validator: (order) => {
        const validOrderOptions = ['asc', 'desc']

        if (!order) return true
        return validOrderOptions.includes(order.toLowerCase())
      },
    },
    /**
     * Indicates the node the tree should begin from
     */
    start: {
      type: SimpleOrgTreeNode,
      default: () => null,
    },
    /**
     * Sets the top level that is displayed in the tree filter dialog's treeview.
     * This can work in tandem with the `lowestLevel` prop.
     *
     * e.g. The org tree has Country -> Division -> Team -> Admin
     *
     * The top level is set to Division.
     *
     * Only Division -> Team -> Admin will be displayed.
     */
    topLevel: {
      type: String,
      default: null,
      validator: (topLevel) => {
        if (!topLevel) return null
        return [
          OrganisationLevel.Country,
          OrganisationLevel.Division,
          OrganisationLevel.Team,
          OrganisationLevel.Admin,
        ].includes(topLevel)
      },
    },
    /**
     * Sets the lowest level that is displayed in the tree filter dialog's treeview.
     * This can work in tandem with the `topLevel` prop.
     *
     * e.g. The org tree has Country -> Division -> Team -> Admin
     *
     * The lowest level is set to Team.
     *
     * Only Country -> Division -> Team will be displayed.
     */
    lowestLevel: {
      type: String,
      default: null,
      validator: (lowestLevel) => {
        if (!lowestLevel) return null
        return [
          OrganisationLevel.Country,
          OrganisationLevel.Division,
          OrganisationLevel.Team,
          OrganisationLevel.Admin,
        ].includes(lowestLevel)
      },
    },
    /**
     * Used to preselect either an action button or a custom list of filter
     * options.
     */
    preselect: {
      type: [String, Array],
      default: () => null,
      validator: (preselectVal) => {
        if (Array.isArray(preselectVal)) {
          if (isNullOrEmptyArray(preselectVal)) return true
          return preselectVal.every((e) => e instanceof SimpleOrgTreeNode)
        }

        if (!preselectVal) return null
        return [
          OrganisationLevel.Country,
          OrganisationLevel.Division,
          OrganisationLevel.Team,
          OrganisationLevel.Admin,
          MINE_LEVEL,
        ].includes(preselectVal)
      },
    },
  },
  data() {
    return {
      treeLevel: TREE_LEVEL,
      /**
       * Tracks the state of the tree filter dialog
       */
      isDialogShowing: false,
      /**
       * Tracks the currently selected button on the button toggle
       */
      selectedItem: 0,
      /**
       * Temporary list. Stores the currently selected items when viewing the tree selector dialog
       */
      currentlySelected: [],
      /**
       * Stores the saved tree selection.
       */
      savedTreeSelection: [],
      /**
       * Search query to filter the tree view
       */
      search: '',
      /**
       * Search filter for the multiple level dropdown menu
       */
      levelSearch: '',
      /**
       * Handles the state of the tree view and if all items are expanded or collapsed
       */
      expanded: false,
      /**
       * The keys that reference controls within the `viewControls` data prop
       */
      viewKeys: ViewKeys,
      /**
       * Contains a list of all the booleans that contain the currently displayed
       * view within the tree filter dialog.
       */
      viewControls: initViewControls(),
      /**
       * Tracks the current filter target for the action button filters for each level.
       * It instructs which node will be selected for filtering based on which button is clicked.
       * Initialised during the created() hook
       */
      selectedActionIds: {
        [OrganisationLevel.Country]: null,
        [OrganisationLevel.Division]: null,
        [OrganisationLevel.Team]: null,
      },
      levelMenuState: {
        [OrganisationLevel.Country]: false,
        [OrganisationLevel.Division]: false,
        [OrganisationLevel.Team]: false,
      },
      /**
       * Stores the last emitted filter update. Will be compared upon next update
       * and will prevent emits if the same payload is being sent
       */
      lastEmit: null,
      /**
       * Object that contains all values that can be set within the preset form
       */
      presetForm: {
        id: null,
        title: null,
        favourite: false,
        selection: null,
      },
      /**
       * Tracks validation state of the preset form
       */
      isPresetFormValid: false,
      /**
       * Preset selected on the manage presets table
       */
      selectedPreset: [],
      /**
       * Indicates if a request is being made to the API to fetch the org tree
       */
      loadingOrgTree: false,
    }
  },
  computed: {
    ...mapGetters({
      organisationTree: 'staff/organisationTree',
      getPresetByKey: 'orgTree/getPresetByKey',
      presets: 'orgTree/presets',
      getUserNameById: 'users/getUserNameById',
    }),
    orderAsc() {
      return this.orderActions === 'asc'
    },
    /**
     * Returns a list of objects that are used to generate the action buttons
     * within the button toggle group.
     *
     * If multiple nodes exist for a specific level, a dropdown menu will be
     * generated for users to select from.
     *
     * These buttons will be filtered by the following properties:
     * - orderActions: Will list the buttons in ascending or descending order (default: asc)
     * - hideActions: Hidden actions will not be rendered
     */
    compActionButtons() {
      const actionButtons = []

      let actions = [
        MINE_LEVEL,
        OrganisationLevel.Team,
        OrganisationLevel.Division,
        OrganisationLevel.Country,
      ]
      if (!this.orderAsc) actions = actions.reverse()

      for (let i = 0; i < actions.length; ++i) {
        if (!this.showActionBtn(actions[i])) continue

        actionButtons.push({
          key: `action-btn-${actions[i].toLowerCase()}`,
          title: this.determineButtonTextBasedOnSelectedId(actions[i]),
          tooltip: this.determineButtonTooltipBasedOnSelectedId(actions[i]),
          level: actions[i],
          type: 'action',
        })

        // Display a dropdown menu button to allow users to filter from a list of
        // items on a given level if there are multiple
        if (this.determineIfMultipleLevelsExist(actions[i])) {
          actionButtons.push({
            key: `action-btn-${actions[i].toLowerCase()}-menu`,
            title: null,
            tooltip: null,
            level: actions[i],
            type: 'menu',
          })
        }
      }

      return actionButtons
    },
    /**
     * Indicates if the user has made any selections within the treeview dialog
     */
    hasSelection() {
      return this.currentlySelected.length > 0
    },
    /**
     * Filters selection within the treeview dialog by a search filter
     */
    searchableSelection() {
      if (!this.searchable) return this.filteredCurrentlySelectedList

      const trimmedSearchTerm = this.search?.toLowerCase()?.trim() || ''

      if (!trimmedSearchTerm) return this.filteredCurrentlySelectedList

      return this.filteredCurrentlySelectedList.filter((selection) =>
        selection.title.toLowerCase().trim().includes(trimmedSearchTerm)
      )
    },
    /**
     * Indicates if any items were returned when filtering the selection by the
     * search term within the treeview dialog
     */
    hasSearchableSelection() {
      if (!this.search?.trim()) return true
      return this.searchableSelection.length > 0
    },
    /**
     * Generates the text to be rendered on the blank tree selection preview
     * action button.
     *
     * Text will be shown when the user makes a selection from the treeview or
     * applies a preset.
     */
    compTreeSelectionPreviewBtnText() {
      if (isNullOrEmptyArray(this.savedTreeSelection)) return ''

      if (this.savedTreeSelection.length === 1) {
        return this.savedTreeSelection[0]?.title
      }

      return this.$t('general.orgFilter.actionBtn.treePreviewText', [
        this.filteredSavedSelectionList.length,
      ])
    },
    getPrimaryTeam() {
      return this.getNodesByLevel(
        OrganisationLevel.Team,
        this.organisationTree
      )?.find((team) => team.isPrimary)
    },
    isTeamFilterEnabled() {
      return this.showActionBtn(OrganisationLevel.Team)
    },
    /**
     * Filters the organisation tree based on props passed into the component.
     *
     * The following props will filter the org list:
     * - start: sets a specific node as the top most node of the tree
     * - topLevel: sets which level is the top most level of the tree, any levels above are pruned
     * - lowestLevel: sets which level is the lowest level in the tree, any levels below are pruned
     */
    processedOrgTree() {
      let orgTree = this.organisationTree

      if (this.start) {
        const foundStartNode = searchTree(
          this.start.id,
          this.start.level,
          orgTree
        )

        if (foundStartNode) orgTree = [foundStartNode]
      }

      if (this.topLevel) {
        orgTree = this.getNodesByLevel(this.topLevel, orgTree)
      }

      if (this.lowestLevel) {
        orgTree = pruneChildrenNodesAtLevel(this.lowestLevel, orgTree)
      }

      return orgTree
    },
    showChipSelectionView() {
      return this.viewControls[ViewKeys.ChipSelection]
    },
    showSavePresetForm() {
      return this.viewControls[ViewKeys.SavePreset]
    },
    showPresetSaveSuccessView() {
      return this.viewControls[ViewKeys.PresetSaveSuccess]
    },
    showManagePresetsView() {
      return this.viewControls[ViewKeys.ManagePresets]
    },
    showDeletePresetConfirmation() {
      return this.viewControls[ViewKeys.DeletePreset]
    },
    showTreeView() {
      for (const viewKey of Object.keys(this.viewControls)) {
        if (this.viewControls[viewKey]) return false
      }

      return true
    },
    isEditingPreset() {
      if (!this.presetForm || !this.presetForm?.key) return false
      return !!this.getPresetByKey(this.presetForm.key)
    },
    /**
     * Returns a list of objects that are used to generate the dialog action
     * buttons. These buttons are contextually rendered based on the current
     * view being displayed within the dialog.
     */
    compDialogActionButtons() {
      const backBtn = {
        key: 'back-btn',
        title: this.$t('general.back'),
        disabled: false,
        color: null,
        text: true,
        event: () => null,
      }

      if (this.showManagePresetsView) {
        return [
          { ...backBtn, ...{ event: () => this.closePresetMenu() } },
          {
            key: 'apply-preset-btn',
            title: this.$t('general.orgFilter.dialog.actionBtn.applyPreset'),
            disabled: this.selectedPreset.length === 0,
            color: 'primary',
            text: true,
            event: () => this.applySelectedPreset(),
          },
        ]
      } else if (this.showPresetSaveSuccessView) {
        return [
          {
            ...backBtn,
            ...{
              key: 'close-btn',
              title: this.$t('general.close'),
              event: () => this.close(),
            },
          },
        ]
      } else if (this.showSavePresetForm) {
        return [
          {
            ...backBtn,
            ...{
              key: 'cancel-btn',
              title: this.$t('general.cancel'),
              event: () => this.closeSavePresetForm(),
            },
          },
          {
            key: 'save-preset-btn',
            title: this.$t('general.orgFilter.dialog.actionBtn.savePreset'),
            disabled: !this.isPresetFormValid,
            color: 'primary',
            text: true,
            event: () => this.savePreset(),
          },
        ]
      } else if (this.showTreeView || this.showChipSelectionView) {
        const actions = [
          {
            key: 'clear-all-btn',
            title: this.$t('general.orgFilter.dialog.actionBtn.clearAll'),
            disabled: false,
            color: null,
            text: true,
            event: () => this.resetSelection(),
          },
        ]

        // Only show save presets button on multi-select
        if (this.multiple) {
          actions.push({
            key: 'save-as-preset-btn',
            title: this.$t('general.orgFilter.dialog.actionBtn.saveAsPreset'),
            disabled: false,
            color: 'primary',
            text: true,
            event: () => this.openSavePresetMenu(null),
          })
        }

        actions.push({
          key: 'save-btn',
          title: this.$t('general.save'),
          disabled: false,
          color: 'primary',
          text: true,
          event: () => this.saveSelection(),
        })

        return actions
      }

      return []
    },
    isLoading() {
      return this.loading || this.loadingOrgTree
    },
    filteredCurrentlySelectedList() {
      return filterDuplicateAdminNodes(this.currentlySelected)
    },
    filteredSavedSelectionList() {
      return filterDuplicateAdminNodes(this.savedTreeSelection)
    },
  },
  watch: {
    /**
     * Watches the dialog state data prop to reset the dialog on close and
     * rehydrate the dialog with the previously saved tree selection on open.
     */
    isDialogShowing: {
      handler() {
        if (!this.isDialogShowing) {
          this.resetSelection()
        } else {
          this.currentlySelected = cloneDeep(this.savedTreeSelection)
        }
      },
    },
  },
  async created() {
    this.loadingOrgTree = true
    const result = await this.loadOrganisationTree()
    this.loadingOrgTree = false

    if (!result.isSuccess) return

    this.initSelectedTeamId()
    this.initSelectedDivsionId()
    this.initSelectedCountryId()

    this.initFilterPreselection()
  },
  destroyed() {
    this.close()
  },
  methods: {
    ...mapActions({
      loadOrganisationTree: 'staff/loadOrganisationTree',
      returnDivisionTreeItemByKey: 'staff/returnDivisionTreeItemByKey',
      upsertPreset: 'orgTree/upsertPreset',
      deletePreset: 'orgTree/deletePreset',
    }),
    initFilterPreselection() {
      if (!this.preselect) return false

      // If an array is passed, it must be a treeview select
      if (Array.isArray(this.preselect)) {
        if (isNullOrEmptyArray(this.preselect)) return false

        this.savedTreeSelection = findNodesByList(
          this.preselect,
          this.processedOrgTree
        )

        this.selectActionFilterByLevel(TREE_LEVEL)

        return
      }

      // Select an action button
      this.selectActionFilterByLevel(this.preselect)
    },
    emitFilterUpdate(payload) {
      // Prevent duplicate filter updates
      if (this.lastEmit) if (isEqual(payload, this.lastEmit)) return

      this.lastEmit = payload

      this.$emit('update', payload)
    },
    getNodesByLevel(targetLevel, tree = []) {
      return getNodesByLevelFunc(targetLevel, tree)
    },
    searchNodesByLevel(targetLevel, search, tree = []) {
      tree = isNullOrEmptyArray(tree) ? this.organisationTree : tree
      return searchNodesByLevelFunc(targetLevel, search, tree)
    },
    /**
     * Sets the currently selected filter and emits filter selection to parent.
     *
     * This is called when clicking on an action button within the filter btn
     * toggle group.
     *
     * @param level Level of node user wants to filter by
     * @param id Optional ID of node user wants to filter by.
     */
    selectActionFilterByLevel(level, id = null) {
      this.selectedItem = `action-btn-${level.toLowerCase()}`

      if (level === MINE_LEVEL)
        return this.emitFilterUpdate({ type: MINE_LEVEL, id: -1 })

      if (level === TREE_LEVEL) {
        const selection = this.multiple
          ? filterDuplicateAdminNodes(this.savedTreeSelection).map((node) => ({
              id: node.id,
              type: node.level,
            }))
          : this.savedTreeSelection

        return this.emitFilterUpdate(selection)
      }

      if (id) {
        this.selectedActionIds[level] = id
      } else if (!this.selectedActionIds[level]) {
        if (level === OrganisationLevel.Team) this.initSelectedTeamId()
        else this.selectedActionIds[level] = this.initSelectedLevel(level)
      }

      this.levelSearch = ''
      this.levelMenuState[level] = false

      this.emitFilterUpdate({
        type: level,
        id: this.selectedActionIds[level],
      })
    },
    selectedTeam() {
      this.selectActionFilterByLevel(OrganisationLevel.Team)
    },
    selectedCountry() {
      this.selectActionFilterByLevel(OrganisationLevel.Country)
    },
    initSelectedTeamId() {
      if (
        !this.isTeamFilterEnabled ||
        this.selectedActionIds[OrganisationLevel.Team]
      )
        return

      if (this.getPrimaryTeam) {
        this.selectedActionIds[OrganisationLevel.Team] = this.getPrimaryTeam?.id
        return
      }

      this.selectedActionIds[OrganisationLevel.Team] = this.initSelectedLevel(
        OrganisationLevel.Team
      )
    },
    initSelectedDivsionId() {
      this.selectedActionIds[OrganisationLevel.Division] =
        this.initSelectedLevel(OrganisationLevel.Division)
    },
    initSelectedCountryId() {
      this.selectedActionIds[OrganisationLevel.Country] =
        this.initSelectedLevel(OrganisationLevel.Country)
    },
    updateSelection(selection) {
      this.currentlySelected = selection
    },
    saveSelection() {
      this.savedTreeSelection = this.currentlySelected

      this.selectActionFilterByLevel(TREE_LEVEL)
      this.close()
    },
    resetSelection() {
      this.currentlySelected = []
    },
    closeChip(selection) {
      this.currentlySelected = this.currentlySelected.filter(
        (selectedItem) => selectedItem.key !== selection.key
      )
    },
    /**
     * Called when dialog is closed to reset the dialog
     */
    close() {
      this.isDialogShowing = false
      this.resetSelection()
      this.toggleView()

      this.presetForm = {
        key: null,
        title: null,
        favourite: false,
        selection: null,
      }

      if (this.expanded) {
        this.toggleTreeview()
      }
    },
    /**
     * Generic function that displays a tooltip over an action button based on
     * level.
     *
     * e.g. Level = Team -> "Team: Vic Teaching" will be the tooltip
     *
     * @param level
     */
    determineButtonTooltipBasedOnSelectedId(level) {
      if (level === MINE_LEVEL)
        return this.$t('general.orgFilter.actionBtn.mineTooltip')

      const id = this.selectedActionIds[level]

      if (!id)
        return this.$t('general.orgFilter.actionBtn.noneSelected', [
          level.toLowerCase(),
        ])

      const found = searchTree(id, level, this.organisationTree)

      if (!found)
        return this.$t('general.orgFilter.actionBtn.noneSelected', [
          level.toLowerCase(),
        ])

      return `${level}: ${found?.title}`
    },
    /**
     * Displays the title of the preselected level for that action button based
     * on level.
     *
     * Often this is the first node at that level.
     *
     * @param level
     */
    determineButtonTextBasedOnSelectedId(level) {
      if (level === MINE_LEVEL) return MINE_LEVEL

      const found = searchTree(
        this.selectedActionIds[level],
        level,
        this.organisationTree
      )

      if (!found) return level

      return found.title
    },
    /**
     * Function to determine if multiple nodes exist at a specific level.
     *
     * This is used to render the dropdown menu next to a level's action button.
     *
     * @param level
     */
    determineIfMultipleLevelsExist(level) {
      if (level === MINE_LEVEL) return false

      const nodes = this.getNodesByLevel(level, this.organisationTree)

      return nodes.length > 1
    },
    /**
     * Function that preselects a node for a specific level. Typically this is
     * the first node available.
     *
     * @param level
     */
    initSelectedLevel(level) {
      const id = this.selectedActionIds[level]

      if (!this.showActionBtn(level)) return null
      if (id) return id

      // Fallback to the first available node
      const teamNodes = this.getNodesByLevel(level, this.organisationTree)

      if (isNullOrEmptyArray(teamNodes)) return null

      return teamNodes[0]?.id
    },
    /**
     * Handles the expand/collapse all toggle interaction within the treeview.
     *
     * It will expand or collapse all nodes within the treeview.
     */
    toggleTreeview() {
      this.expanded = !this.expanded
      this.$refs.orgTreeview.updateAll(this.expanded)
    },
    showActionBtn(action) {
      if (isNullOrEmptyArray(this.hideActions)) return true
      return !this.hideActions.includes(action)
    },
    /**
     * Overrides the selection logic of the button toggle group.
     *
     * It will ignore any attempts to select buttons with a numeric key, as
     * these are often the dropdown menus that we don't want set as selected.
     *
     * @param value
     */
    handleActionButtonToggle(value) {
      if (!isNaN(value)) return

      // Open tree selection dialog if user clicked on the tree selection
      // preview action button but nothing has been selected
      if (
        value === 'action-btn-tree' &&
        isNullOrEmptyArray(this.savedTreeSelection)
      ) {
        this.isDialogShowing = true
        return
      }

      this.selectedItem = value
    },
    isActionBtnActive(key) {
      return this.selectedItem === key
    },
    openSavePresetMenu(key = null) {
      if (key) {
        const foundPreset = this.getPresetByKey(key)

        if (!foundPreset) {
          console.error(this.$t('general.orgFilter.preset.notFound', [key]))
          return
        }

        this.presetForm = cloneDeep(foundPreset)
      } else {
        this.presetForm = {
          key: nanoid(),
          title: generateRandomName(),
          favourite: false,
          selection: cloneDeep(this.currentlySelected).map((selection) => ({
            id: selection.id,
            level: selection.level,
            title: selection.title,
          })),
        }
      }

      this.toggleView(ViewKeys.SavePreset)
    },
    closePresetMenu() {
      this.returnToTreeView()
    },
    closeSavePresetForm() {
      this.isEditingPreset
        ? this.openManagePresetsView()
        : this.returnToTreeView()
    },
    toggleSelectionView() {
      this.toggleView(
        ViewKeys.ChipSelection,
        !this.viewControls[ViewKeys.ChipSelection]
      )
    },
    savePreset() {
      this.upsertPreset(this.presetForm)

      this.toggleView(ViewKeys.PresetSaveSuccess)
    },
    returnToTreeView() {
      this.toggleView()
    },
    /**
     * Handles toggling the views displayed within the treeview dialog.
     *
     * Sets the specified view as displayed while toggling off all other views.
     *
     * Can optionally set the state of the target view.
     *
     * By calling this function with default params, all views will be hidden.
     *
     * @param viewKey {@link ViewKeys}
     * @param state
     */
    toggleView(viewKey = null, state = true) {
      for (const key of Object.keys(this.viewControls)) {
        if (key === viewKey) this.viewControls[key] = state
        else this.viewControls[key] = false
      }
    },
    applyPresetSelection(key, returnAfterApply = false) {
      const foundPreset = this.getPresetByKey(key)

      if (!foundPreset) {
        console.error(this.$t('general.orgFilter.preset.notFound', [key]))
        return
      }

      this.savedTreeSelection = findNodesByList(
        foundPreset.selection,
        this.processedOrgTree
      )

      this.currentlySelected = cloneDeep(this.savedTreeSelection)

      this.selectActionFilterByLevel(TREE_LEVEL)

      toasts.success(
        this.$t('general.orgFilter.preset.appliedToast', [foundPreset.title])
      )

      if (returnAfterApply) this.returnToTreeView()
    },
    /**
     * Applies the selected preset then deselects it afterwards.
     * Triggered from the Manage presets table.
     */
    applySelectedPreset() {
      this.applyPresetSelection(this.selectedPreset[0].key) // Preset selection is single select, will only ever be one
      this.selectedPreset = []
    },
    openDeletePresetConfirmation(key) {
      const foundPreset = this.getPresetByKey(key)

      if (!foundPreset) {
        console.error(this.$t('general.orgFilter.preset.notFound', [key]))
        return
      }

      this.selectedPreset = [foundPreset]

      this.toggleView(ViewKeys.DeletePreset)
    },
    deleteSelectedPreset() {
      this.deletePreset(this.selectedPreset[0].key)

      this.selectedPreset = []
      this.openManagePresetsView()
    },
    openManagePresetsView() {
      this.toggleView(ViewKeys.ManagePresets)
    },
    cancelDeletePreset() {
      this.selectedPreset = []
      this.openManagePresetsView()
    },
    updateFormState(formState) {
      this.isPresetFormValid = formState
    },
    /**
     * Expands all tree nodes upon searching and will close all nodes upon
     * clearing the search
     *
     * @param searchVal
     */
    handleSearch(searchVal) {
      if (!this.showTreeView) return false

      if (searchVal?.trim()) {
        if (!this.expanded) this.toggleTreeview()
      } else {
        if (this.expanded) this.toggleTreeview()
      }
    },
    /**
     * Returns a division's title based on the team Id to help differentiate
     * similar teams within different divisions.
     * @param teamId the team id. It is made up from the team and division id for uniqueness purposes. e.g. 12_10
     */
    getDivisionTitleFromTeamId(teamId) {
      if (!teamId) return ''

      const teamDivisionSplitIds = teamId.split('_')

      if (teamDivisionSplitIds.length !== 2) return ''

      const DIVISION_ID_INDEX = 1

      const divisionId = teamDivisionSplitIds[DIVISION_ID_INDEX]

      return `- ${
        searchTree(
          divisionId,
          OrganisationLevel.Division,
          this.processedOrgTree
        )?.title || 'Unknown'
      }`
    },
  },
}
</script>

<template>
  <div :class="['org-tree-selector', isSmallOrLower ? 'w-100' : '']">
    <v-btn-toggle
      mandatory
      active-class="no-active"
      :class="[isSmallOrLower ? 'v-btn-toggle--column w-100' : '']"
      :dense="dense"
      @change="handleActionButtonToggle"
    >
      <v-tooltip
        v-for="actionBtn of compActionButtons"
        :key="actionBtn.key"
        :disabled="!actionBtn.tooltip"
        top
      >
        <template v-slot:activator="{ on: onTooltip }">
          <v-btn
            v-if="actionBtn.type === 'action'"
            :disabled="disabled || isLoading"
            :value="actionBtn.key"
            :block="isSmallOrLower"
            :class="[
              'text-caption font-weight-medium',
              isActionBtnActive(actionBtn.key)
                ? 'custom-active grey lighten-2 opacity-1'
                : 'no-active',
            ]"
            v-on="onTooltip"
            @click="selectActionFilterByLevel(actionBtn.level)"
          >
            {{ actionBtn.title | truncateText(15) }}
          </v-btn>
          <v-menu
            v-else-if="actionBtn.type === 'menu'"
            v-model="levelMenuState[actionBtn.level]"
            offset-y
            rounded="lg"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on: onMenu }">
              <v-btn
                :disabled="disabled || isLoading"
                icon
                :class="['py-2 grey', isDarkMode ? '' : 'lighten-4']"
                :block="isSmallOrLower"
                :height="dense ? '36px' : '48px'"
                small
                v-on="{ ...onTooltip, ...onMenu }"
              >
                <v-icon small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-card max-height="300px" scrollable>
              <v-list dense>
                <v-subheader class="ml-2">{{
                  $t('general.orgFilter.actionBtn.dropdown.menuSubtitle', [
                    actionBtn.level.toLowerCase(),
                  ])
                }}</v-subheader>
                <v-list-item>
                  <v-text-field
                    v-if="searchable"
                    v-model="levelSearch"
                    :label="$t('treeSelector.searchLabel')"
                    clearable
                    hide-details
                    class="mb-2"
                    solo-inverted
                    flat
                    dense
                    prepend-inner-icon="mdi-magnify"
                    clear-icon="mdi-close-circle"
                  />
                </v-list-item>

                <v-list-item
                  v-for="(item, index) in searchNodesByLevel(
                    actionBtn.level,
                    levelSearch
                  )"
                  :key="index"
                  @click="selectActionFilterByLevel(actionBtn.level, item.id)"
                >
                  <v-list-item-title
                    >{{ item.title }}
                    {{ getDivisionTitleFromTeamId(item.id) }}</v-list-item-title
                  >
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </template>
        {{ actionBtn.tooltip }}
      </v-tooltip>

      <TruncatedChipTooltip :items="filteredSavedSelectionList">
        <template v-slot="{ onTooltip }">
          <v-btn
            v-show="showActionBtn(treeLevel) && savedTreeSelection.length"
            :disabled="disabled || isLoading"
            variant="tonal"
            :class="[
              'text-caption font-weight-medium',
              isActionBtnActive('action-btn-tree')
                ? 'custom-active grey lighten-2 opacity-1'
                : 'no-active',
            ]"
            :block="isSmallOrLower"
            value="action-btn-tree"
            v-on="onTooltip"
            @click="
              savedTreeSelection.length === 0
                ? null
                : selectActionFilterByLevel(treeLevel)
            "
          >
            {{ compTreeSelectionPreviewBtnText | truncateText(15) }}
          </v-btn>
        </template>
      </TruncatedChipTooltip>

      <v-btn
        v-if="showActionBtn(treeLevel)"
        icon
        active-class="no-active"
        :loading="isLoading"
        :disabled="disabled"
        :block="isSmallOrLower"
        @click="isDialogShowing = true"
      >
        <v-icon small>mdi-dots-vertical</v-icon>
      </v-btn>
    </v-btn-toggle>

    <v-dialog
      v-model="isDialogShowing"
      scrollable
      max-width="800"
      @click:outside="close"
    >
      <v-card height="740" :loading="isLoading">
        <div class="text-h6 blue pa-5">
          <div class="d-flex justify-space-between">
            <p class="mb-0 white--text">{{
              $t('general.orgFilter.dialog.header.title')
            }}</p>
            <v-spacer />
            <v-btn
              icon
              dark
              class="rounded-lg"
              style="width: 42px; height: 42px"
              @click="close"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="d-flex align-center justify-space-between mt-4">
            <v-text-field
              v-if="searchable"
              v-model="search"
              dark
              :label="$t('treeSelector.searchLabel')"
              color="white"
              clearable
              hide-details
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              clear-icon="mdi-close-circle"
              @input="handleSearch"
            />
            <v-btn
              v-if="multiple"
              style="height: 42px; padding-top: 2px"
              :disabled="showSavePresetForm"
              outlined
              text
              dark
              class="ml-2 d-flex justify-center"
              @click="toggleSelectionView"
            >
              {{
                $t('general.orgFilter.dialog.currentlySelectedBtn', [
                  filteredCurrentlySelectedList.length,
                ])
              }}
              <v-icon right>{{
                showChipSelectionView ? 'mdi-chevron-up' : 'mdi-chevron-down'
              }}</v-icon>
            </v-btn>
            <v-tooltip v-if="multiple" top>
              <template v-slot:activator="{ on: onTooltip }">
                <v-btn
                  v-if="multiple"
                  icon
                  dark
                  text
                  class="rounded-lg ml-2"
                  outlined
                  style="width: 42px; height: 42px; padding-top: 2px"
                  v-on="onTooltip"
                  @click="openManagePresetsView"
                >
                  <v-icon small>mdi-folder-multiple</v-icon>
                </v-btn>
              </template>
              {{ $t('general.orgFilter.dialog.header.managePresetsTooltip') }}
            </v-tooltip>
          </div>
        </div>

        <v-card-text v-if="showManagePresetsView" class="px-5 pt-3">
          <ManagePresetsTable
            v-model="selectedPreset"
            :search="search"
            @edit="openSavePresetMenu"
            @delete="openDeletePresetConfirmation"
          />
        </v-card-text>

        <v-card-text
          v-if="showDeletePresetConfirmation"
          class="px-5 d-flex justify-center align-center"
        >
          <DeletePresetConfirmationView
            :preset="selectedPreset[0]"
            @cancel="cancelDeletePreset"
            @delete="deleteSelectedPreset"
          />
        </v-card-text>

        <v-card-text
          v-if="showPresetSaveSuccessView"
          class="px-5 d-flex justify-center align-center"
        >
          <PresetSavedSuccessView
            :preset-form="presetForm"
            @back="returnToTreeView"
            @apply="applyPresetSelection(presetForm.key, true)"
          />
        </v-card-text>

        <v-card-text v-if="showSavePresetForm" class="px-5 pt-3">
          <SavePresetForm
            v-model="presetForm"
            :search="search"
            @validate="updateFormState"
          />
        </v-card-text>

        <v-card-text v-if="showChipSelectionView">
          <EmptyStateBlock
            v-if="!hasSelection"
            :title="$t('general.orgFilter.chipSelection.empty.title')"
            :description="
              $t('general.orgFilter.chipSelection.empty.description')
            "
          >
            <template v-slot:button>
              <v-btn text outlined @click="toggleSelectionView">
                <v-icon left>mdi-arrow-left</v-icon> Go back
              </v-btn>
            </template>
          </EmptyStateBlock>
          <EmptySearchResultsBlock
            v-else-if="!hasSearchableSelection"
            :search="search"
            @clear="search = ''"
          />
          <div v-else class="mt-2">
            <TruncatedChipList
              :items="searchableSelection"
              closeable
              @closeChip="closeChip"
            />
          </div>
        </v-card-text>

        <div v-if="showTreeView">
          <v-card-text
            :class="[
              'py-1 px-5 d-flex grey',
              isDarkMode ? 'lighten-1' : 'lighten-5',
            ]"
          >
            <v-spacer></v-spacer>
            <v-btn
              text
              :title="
                expanded
                  ? $t('general.orgFilter.treeview.collapseAllTooltip')
                  : $t('general.orgFilter.treeview.expandAllTooltip')
              "
              icon
              x-small
              @click="toggleTreeview"
              ><v-icon>{{
                expanded ? 'mdi-minus-box-multiple' : 'mdi-plus-box-multiple'
              }}</v-icon></v-btn
            >
          </v-card-text>
          <v-divider></v-divider>
        </div>

        <v-card-text v-if="showTreeView" class="px-3">
          <v-treeview
            ref="orgTreeview"
            v-model="currentlySelected"
            :activatable="!multiple"
            rounded
            color="primary"
            :selection-type="selectionType"
            :search="search"
            :selectable="multiple"
            :items="processedOrgTree"
            :open-all="expanded"
            item-key="key"
            item-text="title"
            return-object
            expand-icon="mdi-chevron-down"
            @update:active="updateSelection"
          ></v-treeview>
        </v-card-text>

        <v-divider v-if="compDialogActionButtons.length > 0"></v-divider>

        <v-card-actions v-if="compDialogActionButtons.length > 0" class="px-5">
          <v-spacer></v-spacer>
          <v-btn
            v-for="action of compDialogActionButtons"
            :key="action.key"
            :color="action.color"
            :disabled="action.disabled"
            :text="action.text"
            @click="action.event"
          >
            {{ action.title }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.org-tree-selector {
  margin-bottom: 20px;
}

// Remove default active styling for buttons within btn toggle
::v-deep .v-btn--active.no-active:not(:hover)::before {
  opacity: 0 !important;
}

::v-deep .v-btn-toggle > .v-btn.v-btn--active {
  opacity: 0.8 !important;
}

// Displays the filter vertically (for responsiveness)
::v-deep .v-btn-toggle {
  &.v-btn-toggle--column {
    flex-direction: column;
  }
}
</style>
